import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  CALIBRATE_COLORS,
  CALIBRATE_FONT_FAMILY_ACCENT,
  CALIBRATE_RADIUS_MD,
} from "components/hoc/Theme";
import { SHELL_MAIN_CHROME_PADDING_PX } from "config";
import { managementTheme } from "./managementTheme";

interface ManagementPageShellProps {
  /** Visible page title rendered on the grey chrome. */
  title: ReactNode;
  /** Right side of the title row (e.g. a Back button). */
  headerExtra?: ReactNode;
  /** Rendered on the grey chrome between the title row and the white panel. */
  beforePanel?: ReactNode;
  /** Content of the white panel (rendered inside its padded inner box). */
  children: ReactNode;
}

/**
 * Calibrate management-page chrome: grey backdrop + in-page title + white
 * rounded panel, all under the shared management theme. Extracted from
 * UserManagementPage so every management page renders identically. Expects
 * to fill the viewport (use with `<Layout noScroll>`); scrolling belongs to
 * the panel content.
 */
export default function ManagementPageShell({
  title,
  headerExtra,
  beforePanel,
  children,
}: ManagementPageShellProps) {
  const chromePad = `${SHELL_MAIN_CHROME_PADDING_PX}px`;

  const titleNode = (
    <Typography
      sx={{
        fontSize: 24,
        fontWeight: 700,
        mb: headerExtra ? 0 : 1.5,
        color: "text.primary",
        fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
        flexShrink: 0,
      }}
    >
      {title}
    </Typography>
  );

  return (
    <ThemeProvider theme={managementTheme}>
      {/* Grey chrome area */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          alignSelf: "stretch",
          display: "flex",
          flexDirection: "column",
          bgcolor: CALIBRATE_COLORS.chromeBackground,
          p: chromePad,
          boxSizing: "border-box",
          minHeight: 0,
        }}
      >
        {headerExtra ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.5,
              flexShrink: 0,
            }}
          >
            {titleNode}
            {headerExtra}
          </Box>
        ) : (
          titleNode
        )}

        {beforePanel}

        {/* White panel */}
        <Box
          sx={(theme) => ({
            bgcolor: "background.paper",
            borderRadius: `${CALIBRATE_RADIUS_MD}px`,
            boxShadow: theme.shadows[2],
            width: "100%",
            flex: 1,
            minHeight: 0,
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            overflow: "hidden",
          })}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              px: { xs: 2, sm: 3 },
              pt: 2,
              pb: 2,
              boxSizing: "border-box",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
